import React, { useEffect, useState } from "react";
import { api, LOGO_URL } from "@/lib/api";

const fmt = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDisplay() {
  const [d, setD] = useState(null);
  const [branchRT, setBranchRT] = useState("");
  const [branchName, setBranchName] = useState("");
  const [images, setImages] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cfd/config");
        setBranchRT(data.running_text || "");
        setBranchName(data.branch_name || "");
        setImages(data.image_urls || []);
      } catch (e) { /* keep defaults */ }
    })();
  }, []);

  useEffect(() => {
    if (images.length <= 1) { setSlide(0); return; }
    const t = setInterval(() => setSlide((s) => (s + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images]);

  useEffect(() => {
    let bc;
    try { bc = new BroadcastChannel("jjl-cfd"); bc.onmessage = (e) => setD(e.data); } catch (err) { /* no BC */ }
    const onStorage = (e) => { if (e.key === "jjl_cfd_state" && e.newValue) { try { setD(JSON.parse(e.newValue)); } catch (err) {} } };
    window.addEventListener("storage", onStorage);
    try { const s = localStorage.getItem("jjl_cfd_state"); if (s) setD(JSON.parse(s)); } catch (err) {}
    return () => { try { bc && bc.close(); } catch (err) {} window.removeEventListener("storage", onStorage); };
  }, []);

  const items = d?.items || [];
  const hasCart = items.length > 0;
  const runningText = d?.running_text || branchRT || "Selamat datang di DA LE TANG HuoGuo!";

  return (
    <div className="flex h-screen flex-col bg-white text-neutral-900" data-testid="customer-display">
      <div className="flex items-center justify-between border-b border-neutral-200 bg-crimson/5 px-6 py-3 lg:px-10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-11 w-11 object-contain lg:h-12 lg:w-12" />
          <div>
            <div className="font-display text-xl font-extrabold tracking-tight text-crimson lg:text-2xl">DA LE TANG HuoGuo</div>
            <div className="text-xs text-muted-foreground lg:text-sm">{d?.branch_name || branchName || "Cabang"}{d?.mode === "dinein" && d?.table ? ` · Meja ${d.table}` : ""}</div>
          </div>
        </div>
        {d?.member ? (
          <div data-testid="cfd-member" className="rounded-xl bg-yellow-400/15 px-4 py-2 text-right">
            <div className="text-[10px] uppercase tracking-widest text-yellow-600 lg:text-xs">Member</div>
            <div className="text-sm font-bold text-neutral-900 lg:text-base">{d.member.name}</div>
            <div className="text-xs text-gold">{Number(d.member.points).toLocaleString("id-ID")} poin</div>
          </div>
        ) : (
          <div className="hidden text-right sm:block">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Pesanan Anda</div>
          </div>
        )}
      </div>

      {!hasCart ? (
        images.length > 0 ? (
          <div className="relative min-h-0 flex-1 overflow-hidden bg-black" data-testid="cfd-standby-image-wrap">
            {images.map((src, i) => (
              <img key={i} src={`${BACKEND}${src}`} alt="" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${i === slide ? "opacity-100" : "opacity-0"}`} />
            ))}
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8 text-center">
            <img src={LOGO_URL} alt="" className="h-32 w-32 animate-pulse object-contain lg:h-44 lg:w-44" />
            <div>
              <div className="font-display text-3xl font-extrabold text-neutral-900 lg:text-4xl">Selamat Datang 🔥</div>
              <p className="mt-2 text-base text-muted-foreground lg:text-xl">Silakan pesan di kasir. Detail pesanan tampil di sini.</p>
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
          <div className="flex-1 overflow-y-auto p-6 lg:p-10">
            <div className="space-y-3">
              {items.map((i, k) => {
                const disc = i.on_discount && i.orig_price > i.price;
                return (
                <div key={k} data-testid={`cfd-item-${k}`} className="flex items-center justify-between rounded-2xl bg-neutral-100 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-crimson font-mono-num text-base font-extrabold text-white lg:h-11 lg:w-11 lg:text-lg">{i.qty}</span>
                    <div>
                      <div className="text-base font-semibold text-neutral-900 lg:text-xl">{i.name}</div>
                      {disc && <div className="text-xs font-semibold text-crimson lg:text-sm">Diskon menu</div>}
                    </div>
                  </div>
                  <div className="text-right">
                    {disc && <div className="font-mono-num text-xs text-muted-foreground line-through lg:text-sm">{fmt(i.orig_price * i.qty)}</div>}
                    <div className="font-mono-num text-base text-gold lg:text-xl">{fmt(i.price * i.qty)}</div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-neutral-200 bg-neutral-50 p-5 lg:w-[380px] lg:border-l lg:border-t-0 lg:p-8">
            <div className="space-y-2 text-base lg:text-lg">
              <Row label="Subtotal" value={fmt(d.subtotal)} />
              {d.special_discount > 0 && <Row label="Diskon Khusus" value={`- ${fmt(d.special_discount)}`} accent="text-emerald-600" />}
              {d.discount > 0 && <Row label={d.voucher_code ? `Voucher ${d.voucher_code}` : "Voucher"} value={`- ${fmt(d.discount)}`} accent="text-emerald-600" />}
              {d.service > 0 && <Row label={`Service Charge${d.service_rate ? ` (${d.service_rate}%)` : ""}`} value={fmt(d.service)} />}
              {d.tax > 0 && <Row label={`PB1${d.tax_rate ? ` (${d.tax_rate}%)` : ""}`} value={fmt(d.tax)} />}
            </div>
            <div className="mt-5 rounded-2xl bg-gold/15 p-4 text-center lg:p-6">
              <div className="text-xs uppercase tracking-widest text-gold lg:text-sm">Total Pembayaran</div>
              <div data-testid="cfd-total" className="mt-1 font-mono-num text-3xl font-extrabold text-gold lg:text-5xl">{fmt(d.total)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden border-t border-neutral-200 bg-crimson px-6 py-3">
        <div className="marquee whitespace-nowrap text-sm font-bold text-white lg:text-base">{runningText}</div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono-num ${accent || "text-neutral-900"}`}>{value}</span>
    </div>
  );
}
